import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { calcularEdadMeses } from '../../utils/helpers';
import { buscarAnimalOffline, submitOffline } from '../../services/offlineHelpers';
import { animalesAPI } from '../../services/api';

const RegistrarPesajeForm = ({ animalSeleccionado, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [animal, setAnimal] = useState(null);
  const [buscarIDE, setBuscarIDE] = useState('');
  const [buscandoAnimal, setBuscandoAnimal] = useState(false);

  // State for creating new animal inline
  const [showNewAnimal, setShowNewAnimal] = useState(false);
  const [newAnimalData, setNewAnimalData] = useState({
    sexo: '',
    raza: 'Brangus',
    categoria: '',
    fecha_nacimiento: '',
    color: '',
  });

  const [formData, setFormData] = useState({
    animal_id: '',
    fecha: new Date().toISOString().split('T')[0],
    peso: '',
    altura_cadera: '',
    frame: '',
    origen: 'manual',
    observaciones: '',
  });

  useEffect(() => {
    if (animalSeleccionado) {
      setAnimal(animalSeleccionado);
      setFormData(prev => ({ ...prev, animal_id: animalSeleccionado.id }));
    }
  }, [animalSeleccionado]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const buscarPorRFID = async () => {
    if (!buscarIDE.trim()) return;
    setBuscandoAnimal(true);
    setError('');
    setShowNewAnimal(false);
    try {
      const result = await buscarAnimalOffline(buscarIDE.trim(), user.establecimiento_id);
      setAnimal(result.animal);
      setFormData(prev => ({ ...prev, animal_id: result.animal.id, origen: result.source === 'cache' ? 'offline' : 'rfid' }));
    } catch {
      // Animal not found — offer to create it
      setShowNewAnimal(true);
      setAnimal(null);
    } finally { setBuscandoAnimal(false); }
  };

  const handleCreateAnimalAndWeigh = async (e) => {
    e.preventDefault();
    if (!newAnimalData.sexo) {
      setError('El sexo es obligatorio para registrar un animal nuevo');
      return;
    }
    if (!formData.peso) {
      setError('El peso es obligatorio');
      return;
    }

    setError('');
    setLoading(true);
    try {
      // Step 1: Create the animal
      const animalPayload = {
        VID: buscarIDE.trim(),
        sexo: newAnimalData.sexo,
        raza: newAnimalData.raza || 'Brangus',
        categoria: newAnimalData.categoria || (newAnimalData.sexo === 'hembra' ? 'vaquilla' : 'novillo'),
        fecha_nacimiento: newAnimalData.fecha_nacimiento || new Date().toISOString().split('T')[0],
        color: newAnimalData.color || null,
        origen: 'propio',
        establecimiento_id: user.establecimiento_id,
      };

      const createRes = await animalesAPI.create(animalPayload);
      const newAnimalId = createRes.data.animal_id;

      // Step 2: Register the pesaje
      const edadMeses = newAnimalData.fecha_nacimiento
        ? calcularEdadMeses(newAnimalData.fecha_nacimiento)
        : 0;

      const pesajePayload = {
        animal_id: newAnimalId,
        establecimiento_id: user.establecimiento_id,
        fecha: formData.fecha,
        edad_meses: edadMeses,
        peso: parseFloat(formData.peso),
        altura_cadera: formData.altura_cadera ? parseFloat(formData.altura_cadera) : null,
        frame: formData.frame ? parseInt(formData.frame) : null,
        origen: 'manual',
        observaciones: formData.observaciones || null,
      };

      const pesajeResult = await submitOffline('/api/pesajes/', pesajePayload);

      if (pesajeResult.online) {
        setSuccessMessage(`Animal ${buscarIDE.trim()} creado y pesaje registrado! (${formData.peso} kg)`);
      } else {
        setSuccessMessage(`Animal creado. ${pesajeResult.message}`);
      }

      // Reset
      setBuscarIDE('');
      setShowNewAnimal(false);
      setNewAnimalData({ sexo: '', raza: 'Brangus', categoria: '', fecha_nacimiento: '', color: '' });
      setFormData({ animal_id: '', fecha: new Date().toISOString().split('T')[0], peso: '', altura_cadera: '', frame: '', origen: 'manual', observaciones: '' });
      setAnimal(null);
      if (onSuccess) setTimeout(() => onSuccess(), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear animal/pesaje');
    } finally { setLoading(false); }
  };

  const handleSubmitExisting = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      const edadMeses = calcularEdadMeses(animal.fecha_nacimiento);
      const payload = {
        animal_id: formData.animal_id,
        establecimiento_id: user.establecimiento_id,
        fecha: formData.fecha,
        edad_meses: edadMeses || 0,
        peso: parseFloat(formData.peso),
        altura_cadera: formData.altura_cadera ? parseFloat(formData.altura_cadera) : null,
        frame: formData.frame ? parseInt(formData.frame) : null,
        origen: 'manual',
        observaciones: formData.observaciones || null,
      };
      const result = await submitOffline('/api/pesajes/', payload);
      if (result.online) {
        setSuccessMessage(`Pesaje registrado! Peso adulto estimado: ${result.data.peso_adulto_estimado}kg`);
      } else {
        setSuccessMessage(result.message);
      }
      setFormData({ animal_id: '', fecha: new Date().toISOString().split('T')[0], peso: '', altura_cadera: '', frame: '', origen: 'manual', observaciones: '' });
      setAnimal(null);
      if (onSuccess) setTimeout(() => onSuccess(), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar pesaje');
    } finally { setLoading(false); }
  };

  const resetAll = () => {
    setAnimal(null);
    setShowNewAnimal(false);
    setBuscarIDE('');
    setError('');
    setFormData(prev => ({ ...prev, animal_id: '' }));
    setNewAnimalData({ sexo: '', raza: 'Brangus', categoria: '', fecha_nacimiento: '', color: '' });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Registrar Pesaje</h2>
        <div className="flex items-center space-x-2">
          {!navigator.onLine && <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold">MODO CAMPO</span>}
          {onCancel && (
            <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" data-testid="pesaje-close-btn">
              &times;
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="pesaje-error">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-testid="pesaje-success">
          {successMessage}
        </div>
      )}

      {/* Search by RFID/VID */}
      {!animal && !animalSeleccionado && !showNewAnimal && (
        <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-800 mb-3" data-testid="scan-title">
            Escanear Caravana / Ingresar VID
          </h3>
          <div className="flex space-x-2">
            <input
              type="text"
              value={buscarIDE}
              onChange={(e) => setBuscarIDE(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarPorRFID()}
              placeholder="Escanea RFID o ingresa VID/IDE..."
              className="flex-1 px-3 py-2 border border-blue-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500"
              autoFocus
              data-testid="pesaje-search-input"
            />
            <button
              onClick={buscarPorRFID}
              disabled={buscandoAnimal}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              data-testid="pesaje-search-btn"
            >
              {buscandoAnimal ? '...' : 'Buscar'}
            </button>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Si el animal no esta registrado, podras crearlo y pesarlo de una sola vez
          </p>
        </div>
      )}

      {/* Animal found — show info */}
      {animal && (
        <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-semibold text-green-800 mb-2" data-testid="animal-found-title">
                Animal Seleccionado
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-medium">VID:</span> {animal.VID}</div>
                <div><span className="font-medium">IDE:</span> {animal.IDE || 'Sin IDE'}</div>
                <div><span className="font-medium">Sexo:</span> {animal.sexo}</div>
                <div><span className="font-medium">Edad:</span> {calcularEdadMeses(animal.fecha_nacimiento)} meses</div>
              </div>
            </div>
            {!animalSeleccionado && (
              <button onClick={resetAll} className="text-red-600 hover:text-red-800 text-sm" data-testid="change-animal-btn">
                Cambiar
              </button>
            )}
          </div>
        </div>
      )}

      {/* NEW: Animal NOT found — create + weigh inline */}
      {showNewAnimal && (
        <form onSubmit={handleCreateAnimalAndWeigh} className="space-y-4">
          <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-amber-800" data-testid="new-animal-title">
                Animal nuevo — VID: <span className="text-lg">{buscarIDE.trim()}</span>
              </h3>
              <button type="button" onClick={resetAll} className="text-gray-500 hover:text-gray-700 text-sm" data-testid="cancel-new-animal-btn">
                Cancelar
              </button>
            </div>
            <p className="text-xs text-amber-700 mb-3">
              Este VID no esta registrado. Completa los datos basicos para crearlo y pesarlo de una vez.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Sexo *</label>
                <select
                  value={newAnimalData.sexo}
                  onChange={(e) => setNewAnimalData(prev => ({ ...prev, sexo: e.target.value }))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  required
                  data-testid="new-animal-sexo"
                >
                  <option value="">Seleccionar...</option>
                  <option value="hembra">Hembra</option>
                  <option value="macho">Macho</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Raza</label>
                <select
                  value={newAnimalData.raza}
                  onChange={(e) => setNewAnimalData(prev => ({ ...prev, raza: e.target.value }))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  data-testid="new-animal-raza"
                >
                  <option value="Angus">Angus</option>
                  <option value="Hereford">Hereford</option>
                  <option value="Brangus">Brangus</option>
                  <option value="Braford">Braford</option>
                  <option value="Limousin">Limousin</option>
                  <option value="Charolais">Charolais</option>
                  <option value="Cruza">Cruza</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={newAnimalData.categoria}
                  onChange={(e) => setNewAnimalData(prev => ({ ...prev, categoria: e.target.value }))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  data-testid="new-animal-categoria"
                >
                  <option value="">Auto (segun sexo)</option>
                  <option value="ternero">Ternero</option>
                  <option value="desmamante">Desmamante</option>
                  <option value="vaquilla">Vaquilla</option>
                  <option value="novillo">Novillo</option>
                  <option value="vaca">Vaca</option>
                  <option value="toro">Toro</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fecha nacimiento</label>
                <input
                  type="date"
                  value={newAnimalData.fecha_nacimiento}
                  onChange={(e) => setNewAnimalData(prev => ({ ...prev, fecha_nacimiento: e.target.value }))}
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  data-testid="new-animal-fecha"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                <input
                  type="text"
                  value={newAnimalData.color}
                  onChange={(e) => setNewAnimalData(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="Negro, Colorado..."
                  className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  data-testid="new-animal-color"
                />
              </div>
            </div>
          </div>

          {/* Peso fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha del Pesaje *</label>
              <input type="date" name="fecha" value={formData.fecha} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required data-testid="new-pesaje-fecha" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Peso (kg) *</label>
              <input type="number" step="0.1" name="peso" value={formData.peso} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required placeholder="Ej: 280" data-testid="new-pesaje-peso" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Altura Cadera (cm)</label>
              <input type="number" step="0.1" name="altura_cadera" value={formData.altura_cadera} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ej: 115" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Frame (1-9)</label>
              <input type="number" min="1" max="9" name="frame" value={formData.frame} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ej: 6" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
            <textarea name="observaciones" value={formData.observaciones} onChange={handleChange} rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Notas..." />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={resetAll} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium" data-testid="create-and-weigh-btn">
              {loading ? 'Guardando...' : 'Crear Animal y Registrar Peso'}
            </button>
          </div>
        </form>
      )}

      {/* Existing animal — weight form only */}
      {animal && (
        <form onSubmit={handleSubmitExisting} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha del Pesaje *</label>
              <input type="date" name="fecha" value={formData.fecha} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Peso (kg) *</label>
              <input type="number" step="0.1" name="peso" value={formData.peso} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required placeholder="Ej: 280" data-testid="existing-pesaje-peso" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Altura Cadera (cm)</label>
              <input type="number" step="0.1" name="altura_cadera" value={formData.altura_cadera} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ej: 115" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Frame (1-9)</label>
              <input type="number" min="1" max="9" name="frame" value={formData.frame} onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ej: 6" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
            <textarea name="observaciones" value={formData.observaciones} onChange={handleChange} rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Notas adicionales..." />
          </div>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">El sistema calcula automaticamente:</p>
            <ul className="text-xs text-yellow-700 mt-1 space-y-1">
              <li>- Peso adulto estimado (segun edad y sexo)</li>
              <li>- Tasa de madurez fisiologica</li>
              <li>- Edad en meses al momento del pesaje</li>
            </ul>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            {onCancel && (
              <button type="button" onClick={onCancel} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Cancelar
              </button>
            )}
            <button type="submit" disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50" data-testid="submit-pesaje-btn">
              {loading ? 'Guardando...' : navigator.onLine ? 'Registrar Pesaje' : 'Guardar Local'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RegistrarPesajeForm;
